// #genai
import React, {
  useRef, useEffect, useState, forwardRef,
} from 'lib/react';
import { useImperativeHandle } from 'react';
import {
  EVGrid,
  EVInputLabel,
  EVTypography,
  EVFormControl,
  EVFormHelperText,
} from 'lib/components';
import { Trans, useTranslation } from 'lib/translation';
import { Link } from 'lib/router'; // #genai
import PropTypes from 'prop-types';
import TextInputField from 'commonComponents/textInputField';
import SelectField from 'commonComponents/select-field';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import { useSelector } from 'lib/redux';
import generateCaptureContext from 'services/microform.service';
import { ACH_AUTHORIZATION_ROUTE } from 'constants/RouteConstants'; // #genai
import history from 'store/CreateHistory'; // #genai
import PaymentInformationStyles from './PaymentInformationStyles';
import PaymentInformationLoader from './Components/PaymentInformationLoader/PaymentInformationLoader.component';
import {
  ACH_ACCOUNT_TYPE_OPTIONS,
  PHASE2_ACH_ACCOUNT_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTION_IDS,
  ACH_SAVED_ROUTING_MASK,
  ACH_PAYMENT_VARIANTS,
} from './PaymentInformationConstants';
import {
  decodeCaptureContext,
  getAchTokenOptions,
} from './PaymentInformation.util';

// #genai Module-level helper: extracts which CyberSource field locations are flagged in
// err.details. Keeping this outside the component removes it from the deeply-nested
// createToken callback and reduces function nesting depth from 5 to 4 (SonarQube S3776).
const getAchErrorLocations = (details) => ({
  hasRoutingErr: details.some((d) => d?.location === 'routingNumber'),
  hasAccountErr: details.some((d) => d?.location === 'accountNumber'),
  hasConfirmErr: details.some((d) => d?.location === 'accountNumberConfirm'),
});

const AchPaymentInformation = forwardRef(({
  isLoading,
  paymentInfo,
  handleChange,
  achConsent,
  handleConsentCheckboxChange,
  paymentInfoErrors,
  displayCardChargeMessage,
  splitCardContainer,
  fullWidthInputFields,
  updateElementRefs,
  achInfo,
  variant,
}, ref) => {
  const classes = PaymentInformationStyles();
  const { t } = useTranslation();
  // #genai Plain <a href="/ach-agreement"> skips the router basename '/myev/' and opens a blank
  // page in test/prod. createHref applies the basename so the new-tab link is /myev/ach-agreement.
  const achAgreementHref = history.createHref({ pathname: ACH_AUTHORIZATION_ROUTE });
  const [microform, setMicroform] = useState(null);
  const [captureContext, setCaptureContext] = useState(null);
  const [widgetError, setWidgetError] = useState('');
  const [isFlexScriptLoaded, setIsFlexScriptLoaded] = useState(false);
  // #genai Bumping this key remounts the three iframe container divs so CyberSource can load fresh
  // hosted fields into clean DOM (used by resetMicroform after a failed save).
  const [microformResetKey, setMicroformResetKey] = useState(0);
  // #genai Always start in edit mode; achInfo arrives async from the saga, so the useEffect below
  // flips to summary mode once it loads (unless the user has already started editing).
  const hasSavedAch = Boolean(achInfo?.accountSuffix);
  const [isEditingAch, setIsEditingAch] = useState(true);
  const isEditingAchRef = useRef(true);
  // #genai Sticky flag once the user touches any field; blocks the achInfo effect from clobbering edits.
  const hasUserStartedEditingRef = useRef(false);
  const achConsentLabel = (
    <Trans
      i18nKey="payment_information__ach_consent"
      components={{
        termsLink: (
          <Link
            style={{ color: '#007CAD', textDecoration: 'none' }}
            to={ACH_AUTHORIZATION_ROUTE}
          >
            terms and conditions
          </Link>
        ),
      }}
    />
  ); // #genai

  // #genai Phase 2 consent copy per Figma 226:26066 — "I have read and agreed to Terms & Conditions".
  // Terms link is #2563EB + underline per the Figma node 226:26119 checkbox label. Opens in a NEW TAB
  // (plain anchor, not react-router Link) so the user keeps their in-progress add-ach form data.
  const achConsentLabelPhase2 = (
    <Trans
      i18nKey="payment_information__ach_consent_phase2"
      components={{
        termsLink: (
          <a
            style={{ color: '#2563EB', textDecoration: 'underline' }}
            href={achAgreementHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms &amp; Conditions
          </a>
        ),
      }}
    />
  ); // #genai

  const currencyTypeId = useSelector(({ userData }) => userData.initParameters?.currencyTypeId);

  const elementRefInfo = {
    firstName: { id: 'firstName', elementRef: useRef(null), message: '' },
    lastName: { id: 'lastName', elementRef: useRef(null), message: '' },
    accountType: { id: 'accountType', elementRef: useRef(null), message: '' },
    routingNumber: { id: 'routingNumber', elementRef: useRef(null), message: '' },
    accountNumber: { id: 'accountNumber', elementRef: useRef(null), message: '' },
    accountNumberConfirm: { id: 'accountNumberConfirm', elementRef: useRef(null), message: '' },
    ccConsent: { id: 'ccConsent', elementRef: useRef(null), message: '' },
  };

  const scriptRef = useRef(null);
  // #genai - Hosted-field styles injected into CyberSource iframes.
  const achHostedFieldStyles = {
    input: {
      'font-size': '14px',
      'font-family': 'helvetica, tahoma, calibri, sans-serif',
      color: '#1B202C',
      // #genai Prevent browser autofill blue inside CyberSource iframes
      background: '#ffffff',
    },
    ':focus': { color: '#1B202C', background: '#ffffff' },
    ':disabled': { cursor: 'not-allowed' },
    valid: { color: '#1B202C', background: '#ffffff' },
    invalid: { color: '#a94442', background: '#ffffff' },
  };

  const generateAndStoreCaptureContext = async () => {
    const response = await generateCaptureContext(currencyTypeId);
    const { captureContext: context } = response;
    if (!context) {
      setWidgetError(t('payment_information__ach_widget_error'));
      return;
    }
    setCaptureContext(context);
  };

  // #genai Clears Redux hosted-field sentinels, tears down stale microform/Flex state, and
  // bumps microformResetKey so iframe containers remount with a fresh capture context.
  //
  // Each handleChange call now produces a PARTIAL dispatch (see handleAchPaymentInfoChange in
  // BillingInformation.component.js): the reducer's spread merge applies them sequentially
  // against the current Redux state, so all three fields end up cleared atomically without
  // the stale-ref race that broke the previous "dispatch whole object" approach.
  const clearHostedFieldsAndReload = () => {
    handleChange('routingNumber', { target: { value: '' } });
    handleChange('accountNumber', { target: { value: '' } });
    handleChange('accountNumberConfirm', { target: { value: '' } });
    // #genai REMOVED single-dispatch workaround — no longer needed after the parent's
    // handleAchPaymentInfoChange was generalized to dispatch partial updates.
    // clearBankFields();
    setMicroform(null);
    setCaptureContext(null);
    setIsFlexScriptLoaded(false);
    setMicroformResetKey((k) => k + 1);
    generateAndStoreCaptureContext();
  };

  // #genai Mark the session user-initiated; on first summary→edit transition, clear hosted fields
  // and reload microform (mirrors Card's isEditingCard flip wiping the card number iframe).
  const enterEditMode = () => {
    const wasInSummary = !isEditingAchRef.current;
    hasUserStartedEditingRef.current = true;
    if (wasInSummary) {
      setIsEditingAch(true);
      isEditingAchRef.current = true;
      clearHostedFieldsAndReload();
    }
  };

  // #genai Hosted ACH fields live in Cybersource iframes; track empty vs filled locally and
  // call enterEditMode so typing in iframes also marks the session as user-initiated.
  const handleHostedFieldChange = (fieldType, event) => {
    enterEditMode();
    handleChange(fieldType, { target: { value: event.empty ? '' : 'entered' } });
  };

  // #genai - Create all hosted fields first, then load into DOM (per CyberSource docs).
  // Phase 2 leaves placeholders blank to match other empty-by-default Phase 2 inputs.
  const setupCheckFields = (microformInstance) => {
    const isPhase2 = variant === ACH_PAYMENT_VARIANTS.Phase2; // #genai
    const routingNumberField = microformInstance.createField('routingNumber', {
      placeholder: isPhase2 ? '' : t('payment_information__routing_number'),
    });
    const accountNumberField = microformInstance.createField('accountNumber', {
      placeholder: isPhase2 ? '' : t('payment_information__account_number'),
    });
    const accountNumberConfirmField = microformInstance.createField('accountNumberConfirm', {
      placeholder: isPhase2 ? '' : t('payment_information__confirm_account_number'),
    });

    routingNumberField.on('change', (event) => handleHostedFieldChange('routingNumber', event));
    accountNumberField.on('change', (event) => handleHostedFieldChange('accountNumber', event));
    accountNumberConfirmField.on('change', (event) => handleHostedFieldChange('accountNumberConfirm', event));

    elementRefInfo.routingNumber.elementRef.current = routingNumberField;
    elementRefInfo.accountNumber.elementRef.current = accountNumberField;
    elementRefInfo.accountNumberConfirm.elementRef.current = accountNumberConfirmField;

    routingNumberField.load('#routingNumber');
    accountNumberField.load('#accountNumber');
    accountNumberConfirmField.load('#accountNumberConfirm');

    setMicroform(microformInstance);
    setWidgetError('');
  };

  const initializeHostedFields = () => {
    if (!globalThis.Flex || !captureContext) {
      return;
    }

    // #genai - Guard against partial renders where hosted field containers are not available yet.
    const hasAllContainers = ['routingNumber', 'accountNumber', 'accountNumberConfirm']
      .every((containerId) => document.getElementById(containerId));
    if (!hasAllContainers) {
      return;
    }

    try {
      const flex = new globalThis.Flex(captureContext);
      const microformInstance = flex.microform('check', { styles: achHostedFieldStyles });
      setupCheckFields(microformInstance);
    } catch (error) {
      setWidgetError(error?.message || t('payment_information__ach_widget_error'));
    }
  };

  // #genai - Always load the Flex library from the current capture context per CyberSource docs;
  // reusing a stale window.Flex from a prior card flow can cause version/config mismatches.
  const loadFlexScript = () => {
    if (!captureContext) {
      return;
    }

    try {
      const decodedContext = decodeCaptureContext(captureContext);
      const { clientLibrary, clientLibraryIntegrity } = decodedContext.ctx[0].data || {};
      if (!clientLibrary) {
        setWidgetError(t('payment_information__ach_widget_error'));
        return;
      }

      if (scriptRef.current) {
        scriptRef.current.remove();
      }

      const script = document.createElement('script');
      script.src = clientLibrary;
      script.type = 'text/javascript';
      script.async = true;
      if (clientLibraryIntegrity) {
        script.integrity = clientLibraryIntegrity;
        script.crossOrigin = 'anonymous';
      }
      script.onload = () => setIsFlexScriptLoaded(true);
      script.onerror = () => {
        setWidgetError(t('payment_information__ach_widget_error'));
      };

      document.head.appendChild(script);
      scriptRef.current = script;
    } catch (error) {
      setWidgetError(error?.message || t('payment_information__ach_widget_error'));
    }
  };

  const handleAchFieldChange = (fieldId, event) => {
    enterEditMode();
    handleChange(fieldId, event);
  };

  const handleAchConsentChange = (...args) => {
    enterEditMode();
    handleConsentCheckboxChange(...args);
  };

  // #genai Extracted to reduce cognitive complexity — handles the edit/summary toggle for each
  // hosted ACH field. Account/confirm fields show the masked suffix in summary mode.
  const renderBankField = (fieldId, hasError = false) => {
    if (isEditingAch) {
      // #genai Phase 2 error: red border + #FFCDD1 fill on the hosted-field iframe (Figma 269:21936).
      const fieldClass = hasError ? `${classes.cardNumberInputText} ${classes.hostedFieldError}` : classes.cardNumberInputText;
      return <div key={microformResetKey} id={fieldId} className={fieldClass} />;
    }
    const isAccountField = fieldId === 'accountNumber' || fieldId === 'accountNumberConfirm';
    let summaryValue;
    if (achInfo?.accountSuffix) {
      summaryValue = isAccountField
        ? `${ACH_SAVED_ROUTING_MASK}${achInfo.accountSuffix}`
        : ACH_SAVED_ROUTING_MASK;
    } else {
      summaryValue = paymentInfo[fieldId];
    }
    return (
      <TextInputField
        inputId={`${fieldId}Summary`}
        inputValue={summaryValue}
        size="small"
        inputLabelProps={{ style: { display: 'none' } }}
        inputFieldProps={{
          fullWidth: true,
          classes: { root: fullWidthInputFields ? classes.cardInputTextFullWidth : classes.cardInputText },
          inputProps: { readOnly: true },
          onFocus: enterEditMode,
          onClick: enterEditMode,
          autoComplete: 'off',
        }}
      />
    );
  };

  // #genai Merged mount-only effects: register element refs and kick off capture context fetch.
  useEffect(() => {
    if (updateElementRefs != null) updateElementRefs(elementRefInfo);
    generateAndStoreCaptureContext();
    return () => {
      if (scriptRef.current) scriptRef.current.remove();
    };
  }, []);

  // #genai Keeps the ref in sync and switches to summary mode once saved ACH data arrives,
  // as long as the user hasn't started editing yet.
  useEffect(() => {
    isEditingAchRef.current = isEditingAch;
    if (hasSavedAch && !hasUserStartedEditingRef.current) {
      setIsEditingAch(false);
      isEditingAchRef.current = false;
    }
  }, [isEditingAch, hasSavedAch]);

  useEffect(() => {
    if (!isLoading && captureContext && isEditingAch) {
      loadFlexScript();
    }
  }, [isLoading, captureContext, isEditingAch]);

  useEffect(() => {
    if (!isLoading && isFlexScriptLoaded && isEditingAch) {
      initializeHostedFields();
    }
  }, [isLoading, isFlexScriptLoaded, captureContext, isEditingAch]);

  const handleTokenisation = async () => {
    // #genai No edit happened (summary mode untouched); skip tokenization and signal saga to
    // PUT only non-bank fields if any were changed.
    if (!isEditingAchRef.current) {
      return {
        paymentMethod: PAYMENT_METHOD_OPTION_IDS.Ach,
        transientToken: null,
        isAchUpdated: false,
      };
    }

    if (!microform) {
      return {
        paymentMethod: PAYMENT_METHOD_OPTION_IDS.Ach,
        transientToken: null,
        isAchUpdated: true,
      };
    }

    // #genai Capture per-field filled state BEFORE tokenize so we can distinguish "user typed
    // something invalid" (specific error) from "user left it empty" (per-field "is required").
    // CyberSource surfaces empty fields in err.details too, so we always pair details with the
    // pre-tokenize sentinel state.
    const routingFilled = !!paymentInfo.routingNumber;
    const accountFilled = !!paymentInfo.accountNumber;
    const confirmFilled = !!paymentInfo.accountNumberConfirm;
    const accountFieldsFilled = accountFilled && confirmFilled;

    const tokenResult = await new Promise((resolve) => {
      microform.createToken(getAchTokenOptions(paymentInfo), (err, token) => {
        if (err) {
          // #genai Don't surface CyberSource's raw err.message (e.g. "One or more fields have a
          // validation error.") as widgetError — it leaks into the inline accountNumber slot
          // when our per-field error is empty. Per-field flags below + saga validation give the
          // user a precise, actionable message instead.
          const details = Array.isArray(err.details) ? err.details : [];
          // #genai Use module-level helper to stay within 4-level nesting depth (SonarQube S3776)
          const { hasRoutingErr, hasAccountErr, hasConfirmErr } = getAchErrorLocations(details);
          // #genai Mismatch: both Acc + Confirm were filled, accountNumberConfirm is in details
          // and accountNumber itself is NOT (i.e. the values just don't agree).
          const isMismatch = accountFieldsFilled && hasConfirmErr && !hasAccountErr;
          // #genai Routing invalid: routing was filled but CyberSource flagged it (bad checksum,
          // non-US routing, etc).
          const isRoutingInvalid = routingFilled && hasRoutingErr;
          // #genai Account invalid (non-mismatch): account was filled and CyberSource flagged it
          // (e.g. too few digits). Mismatch wins to avoid double-labelling the same condition.
          const isAccountInvalid = accountFilled && hasAccountErr && !isMismatch;
          resolve({
            token: null, isMismatch, isRoutingInvalid, isAccountInvalid,
          });
          return;
        }

        setWidgetError('');
        resolve({
          token, isMismatch: false, isRoutingInvalid: false, isAccountInvalid: false,
        });
      });
    });

    return {
      paymentMethod: PAYMENT_METHOD_OPTION_IDS.Ach,
      transientToken: tokenResult.token,
      isAchUpdated: true,
      isAccountNumberMismatch: tokenResult.isMismatch,
      isRoutingNumberInvalid: tokenResult.isRoutingInvalid,
      isAccountNumberInvalid: tokenResult.isAccountInvalid,
    };
  };

  // #genai Called by BillingInformation on tokenization or backend save failure. Forces edit mode,
  // clears the 'entered' sentinels from Redux, tears down the stale microform/Flex state, and bumps
  // microformResetKey so the iframe containers remount with a fresh capture context.
  const resetMicroform = () => {
    hasUserStartedEditingRef.current = true;
    setIsEditingAch(true);
    isEditingAchRef.current = true;
    clearHostedFieldsAndReload();
  };

  useImperativeHandle(ref, () => ({
    tokenize: handleTokenisation,
    resetMicroform,
  }));

  if (isLoading) {
    return (
      <PaymentInformationLoader splitCardContainer={splitCardContainer} fullWidthInputFields={fullWidthInputFields} />
    );
  }

  // #genai Phase 2 layout (Figma 226:26066 / 226:27070 / 226:28535): Account Type first, then the
  // routing number on its own row, account + confirm side-by-side, and the new "Terms & Conditions"
  // consent copy (no ACH charge message). First/Last name and Company now live in the Billing
  // Information section on the Add-Bank page, not here. All hosted-field / tokenize plumbing is
  // shared with Phase 1 below — only the layout differs.
  if (variant === ACH_PAYMENT_VARIANTS.Phase2) { // #genai
    // #genai Drives both the FormControl error state and the rose checkbox outline on the consent row.
    const hasConsentError = paymentInfoErrors.ccConsent?.length > 0;
    return (
      <EVGrid container item direction="column" className={classes.paymentInfoContainer}>
        {/* Account Type + Routing number — side-by-side (Figma 269:20728 row 1, each 380px) */}
        <div className={classes.phase2Row}>
          <EVGrid className={classes.fieldContainer} item style={{ flex: 1 }}>
            {/* #genai Phase 2 (Figma 269:21936): labels keep their default color on error — only the
                input box border/fill and helper text turn red. Matches every other field on the page. */}
            {/* #genai Phase 2 UX: Account Type label matches Routing/Account label color (phase2Label). */}
            <EVInputLabel
              className={classes.phase2Label}
              classes={{ asterisk: classes.asterisk }}
              required
            >
              {t('payment_information__account_type_phase2')}
            </EVInputLabel>
            <SelectField
              selectId="accountType"
              variant="outlined"
              fullWidth
              selectedValue={paymentInfo.accountType}
              handleChange={(event) => handleAchFieldChange('accountType', event)}
              className={classes.cardTypeOptions}
              selectOptions={PHASE2_ACH_ACCOUNT_TYPE_OPTIONS}
              error={paymentInfoErrors.accountType.length > 0}
              errorMessage={paymentInfoErrors.accountType}
              innerRef={elementRefInfo.accountType.elementRef}
            />
          </EVGrid>
          <EVGrid key="routingNumber" className={classes.fieldContainer} item style={{ flex: 1 }}>
            <EVInputLabel
              className={classes.phase2Label}
              classes={{ asterisk: classes.asterisk }}
              required
            >
              {t('payment_information__routing_number_phase2')}
            </EVInputLabel>
            {renderBankField('routingNumber', paymentInfoErrors.routingNumber.length > 0)}
            {paymentInfoErrors.routingNumber && (
              <EVFormHelperText error className={classes.cardInputHelperText}>
                {paymentInfoErrors.routingNumber}
              </EVFormHelperText>
            )}
          </EVGrid>
        </div>

        {/* Account number + Confirm — side-by-side */}
        <div className={classes.phase2Row}>
          <EVGrid key="accountNumber" className={classes.fieldContainer} item style={{ flex: 1 }}>
            <EVInputLabel
              className={classes.phase2Label}
              classes={{ asterisk: classes.asterisk }}
              required
            >
              {t('payment_information__account_number_phase2')}
            </EVInputLabel>
            {renderBankField('accountNumber', Boolean(paymentInfoErrors.accountNumber || widgetError))}
            {(paymentInfoErrors.accountNumber || widgetError) && (
              <EVFormHelperText error className={classes.cardInputHelperText}>
                {paymentInfoErrors.accountNumber || widgetError}
              </EVFormHelperText>
            )}
          </EVGrid>
          <EVGrid key="accountNumberConfirm" className={classes.fieldContainer} item style={{ flex: 1 }}>
            <EVInputLabel
              className={classes.phase2Label}
              classes={{ asterisk: classes.asterisk }}
              required
            >
              {t('payment_information__confirm_account_number_phase2')}
            </EVInputLabel>
            {renderBankField('accountNumberConfirm', paymentInfoErrors.accountNumberConfirm.length > 0)}
            {paymentInfoErrors.accountNumberConfirm && (
              <EVFormHelperText error className={classes.cardInputHelperText}>
                {paymentInfoErrors.accountNumberConfirm}
              </EVFormHelperText>
            )}
          </EVGrid>
        </div>

        {/* Consent — Phase 2 "Terms & Conditions" copy, no ACH charge message */}
        <EVGrid
          className={hasConsentError
            ? `${classes.ccConsentContainer} ${classes.phase2CcConsentError}`
            : classes.ccConsentContainer}
          item
        >
          <EVFormControl error={hasConsentError} component="fieldset">
            <LabelledCheckbox
              checkboxId="saveBankData"
              handleCheckboxSelectChange={handleAchConsentChange}
              checked={achConsent}
              checkboxValue={achConsent}
              checkboxLabel={achConsentLabelPhase2}
              className={classes.checkboxContainer}
              inputRef={elementRefInfo.ccConsent.elementRef}
            />
            <EVFormHelperText className={classes.ccErrorHelperText}>{paymentInfoErrors.ccConsent}</EVFormHelperText>
          </EVFormControl>
        </EVGrid>
      </EVGrid>
    );
  }

  return (
    <EVGrid container item direction="column" className={classes.paymentInfoContainer}>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="firstName"
          inputValue={paymentInfo.firstName}
          size="small"
          handleChange={(event) => handleAchFieldChange('firstName', event)}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: true,
            error: paymentInfoErrors.firstName.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 64 },
            classes: { root: fullWidthInputFields ? classes.cardInputTextFullWidth : classes.cardInputText },
            FormHelperTextProps: { classes: { error: classes.cardInputHelperText } },
            inputRef: elementRefInfo.firstName.elementRef,
            autoComplete: 'off',
          }}
          labelText={t('payment_information__first_name')}
          helperText={paymentInfoErrors.firstName}
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item>
        <TextInputField
          inputId="lastName"
          inputValue={paymentInfo.lastName}
          size="small"
          handleChange={(event) => handleAchFieldChange('lastName', event)}
          inputLabelProps={{
            className: classes.label,
            classes: { asterisk: classes.asterisk },
            required: true,
            error: paymentInfoErrors.lastName.length > 0,
          }}
          inputFieldProps={{
            fullWidth: true,
            inputProps: { maxLength: 64 },
            classes: { root: fullWidthInputFields ? classes.cardInputTextFullWidth : classes.cardInputText },
            FormHelperTextProps: { classes: { error: classes.cardInputHelperText } },
            inputRef: elementRefInfo.lastName.elementRef,
            autoComplete: 'off',
          }}
          labelText={t('payment_information__last_name')}
          helperText={paymentInfoErrors.lastName}
        />
      </EVGrid>
      <EVGrid className={classes.fieldContainer} item style={{ maxWidth: '782px' }}>
        <EVInputLabel
          className={classes.label}
          classes={{ asterisk: classes.asterisk }}
          required
          error={paymentInfoErrors.accountType.length > 0}
        >
          {t('payment_information__account_type')}
        </EVInputLabel>
        <SelectField
          selectId="accountType"
          variant="outlined"
          fullWidth
          selectedValue={paymentInfo.accountType}
          handleChange={(event) => handleAchFieldChange('accountType', event)}
          className={classes.cardTypeOptions}
          selectOptions={ACH_ACCOUNT_TYPE_OPTIONS}
          error={paymentInfoErrors.accountType.length > 0}
          errorMessage={paymentInfoErrors.accountType}
          innerRef={elementRefInfo.accountType.elementRef}
        />
      </EVGrid>
      {/* #genai In summary mode (existing bank account on file), render plain inputs showing the
          masked routing placeholder + accountSuffix from the API. Focusing any of them flips to
          edit mode, replacing them with the CyberSource hosted-field iframes for re-entry. */}
      <EVGrid key="routingNumber" className={classes.fieldContainer} item>
        <EVInputLabel
          className={classes.label}
          classes={{ asterisk: classes.asterisk }}
          required
          error={paymentInfoErrors.routingNumber.length > 0}
        >
          {t('payment_information__routing_number')}
        </EVInputLabel>
        {renderBankField('routingNumber')}
        {paymentInfoErrors.routingNumber && (
          <EVFormHelperText error className={classes.cardInputHelperText}>
            {paymentInfoErrors.routingNumber}
          </EVFormHelperText>
        )}
      </EVGrid>
      <EVGrid key="accountNumber" className={classes.fieldContainer} item>
        <EVInputLabel
          className={classes.label}
          classes={{ asterisk: classes.asterisk }}
          required
          error={paymentInfoErrors.accountNumber.length > 0}
        >
          {t('payment_information__account_number')}
        </EVInputLabel>
        {renderBankField('accountNumber')}
        {(paymentInfoErrors.accountNumber || widgetError) && (
          <EVFormHelperText error className={classes.cardInputHelperText}>
            {paymentInfoErrors.accountNumber || widgetError}
          </EVFormHelperText>
        )}
      </EVGrid>
      <EVGrid key="accountNumberConfirm" className={classes.fieldContainer} item>
        <EVInputLabel
          className={classes.label}
          classes={{ asterisk: classes.asterisk }}
          required
          error={paymentInfoErrors.accountNumberConfirm.length > 0}
        >
          {t('payment_information__confirm_account_number')}
        </EVInputLabel>
        {renderBankField('accountNumberConfirm')}
        {paymentInfoErrors.accountNumberConfirm && (
          <EVFormHelperText error className={classes.cardInputHelperText}>
            {paymentInfoErrors.accountNumberConfirm}
          </EVFormHelperText>
        )}
      </EVGrid>
      <EVGrid className={classes.ccConsentContainer} item>
        {displayCardChargeMessage ? (
          <EVFormControl error={paymentInfoErrors.ccConsent?.length > 0} component="fieldset">
            <LabelledCheckbox
              checkboxId="saveBankData"
              handleCheckboxSelectChange={handleAchConsentChange}
              checked={achConsent}
              checkboxValue={achConsent}
              checkboxLabel={achConsentLabel}
              className={classes.checkboxContainer}
              inputRef={elementRefInfo.ccConsent.elementRef}
            />
            <EVTypography className={classes.cardChargeMessage}>
              {t('payment_information__ach_charge_message')}
            </EVTypography>
            <EVFormHelperText className={classes.ccErrorHelperText}>{paymentInfoErrors.ccConsent}</EVFormHelperText>
          </EVFormControl>
        ) : (
          <LabelledCheckbox
            checkboxId="saveBankData"
            handleCheckboxSelectChange={handleAchConsentChange}
            checked={achConsent}
            checkboxValue={achConsent}
            checkboxLabel={achConsentLabel}
            className={classes.checkboxContainer}
            inputRef={elementRefInfo.ccConsent.elementRef}
          />
        )}
      </EVGrid>
    </EVGrid>
  );
});

AchPaymentInformation.defaultProps = {
  displayCardChargeMessage: false,
  splitCardContainer: false,
  fullWidthInputFields: false,
  updateElementRefs: null,
  achInfo: null,
  variant: ACH_PAYMENT_VARIANTS.Phase1, // #genai
};

AchPaymentInformation.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  paymentInfo: PropTypes.shape().isRequired,
  handleChange: PropTypes.func.isRequired,
  achConsent: PropTypes.bool.isRequired,
  handleConsentCheckboxChange: PropTypes.func.isRequired,
  paymentInfoErrors: PropTypes.shape().isRequired,
  displayCardChargeMessage: PropTypes.bool,
  splitCardContainer: PropTypes.bool,
  fullWidthInputFields: PropTypes.bool,
  updateElementRefs: PropTypes.func,
  achInfo: PropTypes.shape({ accountSuffix: PropTypes.string }),
  variant: PropTypes.oneOf(Object.values(ACH_PAYMENT_VARIANTS)), // #genai
};

export default AchPaymentInformation;
